import React, { useState, memo } from "react";
import { Box, IconButton, Checkbox } from "@material-ui/core";
import LightTooltip from "../common/LightTooltip";
import AddIcon from "@material-ui/icons/Add";
import { makeStyles } from "@material-ui/styles";
import clsx from "clsx";

import StopLeftIcon from "./StopLeftIcon";
import StopInfo from "./StopInfo";
import StopRequirements from "./StopRequirements";
import StopTimeInfo from "./StopTimeInfo";
import { connect } from "react-redux";
import { routeColors } from "../data/constants";
import OptimizedTooltip from "../common/OptimizedTooltip";
import useProductTour from "../../states/tour/tours/product";
import { setHoverStop } from "../../store/actions/map";

const useStyles = makeStyles(theme => ({
  stopWrapper: {
    boxSizing: "border-box",
    borderBottom: "1px solid #DBDCE1",
    display: "flex",
    cursor: "pointer",
    alignItems: "center",
    "&:last-of-type": {
      "& .stop-dots-icon": {
        display: "none"
      }
    },
    "&:hover": {
      backgroundColor: "#F4F4F4"
    }
  },
  highlight: {
    backgroundColor: "#F4F4F4"
  },
  active: {
    backgroundColor: "#F8F9FF"
  },
  stop: {
    "& .edit-remove-btns": {
      opacity: 0,
      transition: "opacity .2s"
    },
    "&:hover .edit-remove-btns": {
      opacity: 1
    },
    "&:hover .pinBox": {
      backgroundColor: "#5f6368"
    },
    "&:hover .lock-icon": {
      color: "#fff"
    },
    "&:hover .icon-time-window": {
      opacity: 1
    }
  },
  stopLocked: {
    // pointerEvents: "none",
    "& .edit-remove-btns": {
      display: "none"
    }
  },
  checkBox: {
    padding: 8,
    margin: theme.spacing(-1)
  },
  btnSmall: {
    padding: 8
  },
  stopFinishedOrSkipped: {
    "& > div > :not(span)": {
      opacity: 0.7
    },
    "&:hover > div > :not(span)": {
      opacity: 1
    }
  }
}));

function Stop({
  isLocked,
  el,
  style,
  projectExpired,
  routeStatus,
  routeId,
  colorsObj,
  isEditing,
  stopInfo,
  editStop,
  hasSolution,
  scheduled,
  plan,
  showAll,
  isScheduled,
  editId,
  isSuggestion,
  isInCurrentSelection,
  handleSelect,
  hasCurrentSelection,
  handleItemSelect,
  finishedOrSkipped,
  suggestionIndex,
  setHaveHovered = () => {},
  haveHovered,
  isActive,
  setHoverStop
}) {
  const classes = useStyles();
  const getColor = id => (colorsObj[id] >= 0 ? colorsObj[id] : 0);
  const productTour = useProductTour();

  const [isHovered, setHovered] = useState(false);

  const onMouseHover = val => {
    setHaveHovered(val);
    setHovered(val);
    if (val && !isEditing) {
      setHoverStop({ stop: el, routeId, snapToRoad: true });
    } else {
      setHoverStop(null);
    }
  };

  return (
    <Box
      position="relative"
      className={clsx(classes.stopWrapper, {
        [classes.active]: !isSuggestion && editId === el.id,
        [classes.highlight]: isActive && !haveHovered
      })}
      onClick={() => {
        if (isSuggestion) {
          handleItemSelect(el);
        } else editStop(el.id, el.status);
      }}
      onMouseEnter={() => onMouseHover(true)}
      onMouseLeave={() => onMouseHover(false)}
      height={56}
      style={style}
      px={3}
    >
      <Box
        display="flex"
        position="relative"
        className={clsx(classes.stop, {
          [classes.stopLocked]: isLocked,
          [classes.stopFinishedOrSkipped]: finishedOrSkipped
        })}
        data-cy="stop"
      >
        {/* Left Icon */}
        <Box display="flex" alignItems="center" pr={2}>
          {!isSuggestion && (hasCurrentSelection || isHovered) ? (
            <OptimizedTooltip title="Select">
              <Checkbox
                checked={isInCurrentSelection}
                onClick={e => e.stopPropagation()}
                onChange={e => {
                  if (!productTour.hasActiveTourCurrently()) {
                    handleSelect(el.id, isInCurrentSelection, isScheduled, e);
                  }
                }}
                // disabled={projectExpired}
                value="checkedB"
                color="primary"
                className={classes.checkBox}
              />
            </OptimizedTooltip>
          ) : (
            <StopLeftIcon
              isLocked={isLocked}
              isEditing={isEditing}
              isSuggestion={isSuggestion}
              color={
                !!routeId
                  ? routeColors[getColor(routeId) % routeColors.length]
                  : isSuggestion
                  ? "#C3C3C4"
                  : "#606165"
              }
            />
          )}
        </Box>

        {/* Input / stop details */}
        <Box width={204}>
          <StopInfo
            isLocked={isLocked}
            stopInfo={stopInfo}
            isSuggestion={isSuggestion}
          />
        </Box>

        {!isSuggestion ? (
          <>
            <Box width={60}>
              <StopRequirements stop={el} />
            </Box>
            <Box
              width={96}
              display="flex"
              flexDirection="column"
              justifyContent="space-between"
              alignItems="flex-end"
            >
              <StopTimeInfo
                unassigned={!hasSolution || !scheduled}
                stop={el}
                routeStatus={routeStatus}
                liveStatusUpdates={plan.ft_live_status_updates}
                showAll={showAll}
              />
            </Box>
          </>
        ) : (
          <Box
            width={156}
            display="flex"
            flexGrow={1}
            justifyContent={"flex-end"}
          >
            <IconButton
              style={{
                pointerEvents: "none"
              }}
            >
              <AddIcon />
            </IconButton>
          </Box>
        )}

        {/* Actions */}
        {/* {!projectExpired && (
          <Box
            alignSelf="center"
            display="flex"
            alignItems="center"
            className="edit-remove-btns"
            position="absolute"
            top={-1}
            right={-2}
          >
            <Link
              component="button"
              onClick={() => editStop(el.id, el.status)}
              className={classes.btnSmall}
              tabIndex="-1"
              disabled={!el.id || isProductTourStepAddingStops}
              data-cy="editStop"
            >
              <OptimizedTooltip placement="bottom" title="Edit stop details">
                <EditIcon
                  style={{
                    color:
                      !el.id || isProductTourStepAddingStops
                        ? "#CFCFCF"
                        : "#808080"
                  }}
                />
              </OptimizedTooltip>
            </Link>

            <Link
              component="button"
              onClick={e => {
                e.preventDefault();
                setConfirmDialogState({ open: true, stop: el });
              }}
              className={classes.btnSmall}
              tabIndex="-1"
              disabled={
                finishedOrSkipped ||
                (!notFirstSecondStops && !el.id) ||
                isProductTourStepAddingStops
              }
              data-cy="deleteStop"
            >
              <OptimizedTooltip placement="bottom" title="Delete stop">
                <DeleteIcon
                  style={{
                    color:
                      finishedOrSkipped ||
                      (!notFirstSecondStops && !el.id) ||
                      isProductTourStepAddingStops
                        ? "#CFCFCF"
                        : "#808080"
                  }}
                />
              </OptimizedTooltip>
            </Link>
          </Box>
        )} */}
      </Box>
    </Box>
  );
}

const mapStateToProps = (state, props) => ({
  hasSolution: !!state.project.solution,
  scheduled:
    state.project.solution &&
    state.project.solution.unassigned.stops.findIndex(
      task => task.id === props.el.id
    ) === -1,
  colorsObj: state.project.solution && state.project.solution.routeColors,
  plan:
    (state.auth.userData.organisationSite &&
      state.auth.userData.organisationSite.organisation.plan) ||
    {},
  showAll: state.project.solution?.showAll
});

export default connect(mapStateToProps, { setHoverStop })(memo(Stop));
